import axios from "axios";
import dayjs from "dayjs";

function sortKeysRecursively(obj) {
  if (Array.isArray(obj)) {
    return obj.map(sortKeysRecursively);
  } else if (obj !== null && typeof obj === "object") {
    return Object.keys(obj)
      .sort()
      .reduce((sortedObj, key) => {
        sortedObj[key] = sortKeysRecursively(obj[key]);
        return sortedObj;
      }, {});
  }
  return obj;
}

async function getCountsData(url, cid, accessToken, payload) {
  try {
    let metricsKey = Object.keys(payload.metrics)[0];
    let allData = [];
    let nextToken = payload.metrics[metricsKey].next_token || null;
    do {
      payload.metrics[metricsKey].next_token = nextToken;
      let response = await axios.post(
        `${url}/v2/cids/${cid}/metrics/batch/query`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      let data = response.data.data[metricsKey];
      allData = allData.concat(data.data);
      nextToken = data.next_token;
    } while (nextToken);
    return allData;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
}

function generateCountsTimeRangeByDate(date, startHour = 0, endHour = 23, interval = 3) {
  if (!dayjs(date, "YYYYMMDD", true).isValid()) {
    throw new Error("Invalid date format. Expected YYYYMMDD.");
  }
  const validStartHour = Math.max(0, Math.min(startHour, 23));
  const validEndHour = Math.max(0, Math.min(endHour, 23));
  if (validStartHour > validEndHour) {
    throw new Error("startHour cannot be greater than endHour.");
  }
  const currentUtcDate = dayjs().utc().format("YYYYMMDD");
  const currentUtcHour = dayjs().utc().hour();
  const previousUtcDate = dayjs().utc().subtract(1, "day").format("YYYYMMDD");

  const formattedDate = dayjs(date, "YYYYMMDD").format("YYYY-MM-DD");
  const timeRanges = [];
  const padHour = (hour) => String(hour).padStart(2, "0");
  const addRange = (start, end) => {
    return {
      start_time: `${formattedDate} ${padHour(start)}:00:00`,
      end_time: `${formattedDate} ${padHour(end)}:59:59`,
    };
  };

  if (date === currentUtcDate) {
    if (currentUtcHour >= interval) {
      const range1End = currentUtcHour - interval;
      if (validStartHour <= range1End) {
        if (validEndHour <= range1End) {
          timeRanges.push(addRange(validStartHour, validEndHour), {});
        } else {
          timeRanges.push(
            addRange(validStartHour, range1End),
            addRange(range1End + 1, Math.min(currentUtcHour, validEndHour))
          );
        }
      } else {
        timeRanges.push(
          {},
          addRange(validStartHour, Math.min(currentUtcHour, validEndHour))
        );
      }
    } else {
      timeRanges.push(
        {},
        addRange(validStartHour, Math.min(currentUtcHour, validEndHour))
      );
    }
  } else if (date === previousUtcDate) {
    if (currentUtcHour >= interval - 1) {
      timeRanges.push(addRange(validStartHour, validEndHour), {});
    } else {
      const range1End = 24 + currentUtcHour - interval;
      if (validStartHour <= range1End) {
        if (validEndHour <= range1End) {
          timeRanges.push(addRange(validStartHour, validEndHour), {});
        } else {
          timeRanges.push(
            addRange(validStartHour, range1End),
            addRange(range1End + 1, validEndHour)
          );
        }
      } else {
        timeRanges.push({}, addRange(validStartHour, validEndHour));
      }
    }
  } else {
    timeRanges.push(addRange(validStartHour, validEndHour), {});
  }

  return timeRanges;
}

function generateHourAndRawMetricsTimeRanges(dates, isCC = false) {
  const metricsNames = isCC
    ? ["aut_count_cc_hour", "aut_count"]
    : ["aut_count_hour", "aut_count"];

  let timeRanges = dates
    .map((date) => {
      let dateTimeRanges = generateCountsTimeRangeByDate(date);
      return dateTimeRanges.map((range, index) => ({
        ...range,
        metrics_name: metricsNames[index],
      }));
    })
    .flat();
  return timeRanges.filter(
    (item) => item.start_time && item.end_time && item.metrics_name
  );
}

function generateDayAndHourAndRawMetricsTimeRanges(
  fromDate,
  toDate,
  startHour = "00",
  endHour = "23",
  isCC = false
) {
  let timeRanges = [];
  let currentUtcDate = dayjs().utc().format("YYYYMMDD");
  if (
    fromDate > toDate ||
    fromDate > currentUtcDate ||
    (fromDate === toDate && startHour > endHour)
  ) {
    return timeRanges;
  }
  if (toDate > currentUtcDate) {
    toDate = currentUtcDate;
    endHour = "23";
  }
  let dayMetrics = isCC ? "aut_count_cc_day" : "aut_count_day";
  let hourMetrics = isCC ? "aut_count_cc_hour" : "aut_count_hour";
  let rawMetrics = "aut_count";
  let previousUtcDate = dayjs().utc().subtract(1, "day").format("YYYYMMDD");
  let targetDates = [];
  if (fromDate !== currentUtcDate && fromDate !== previousUtcDate) {
    if (fromDate === toDate) {
      timeRanges.push({
        start_time: `${dayjs(fromDate, "YYYYMMDD").format(
          "YYYY-MM-DD"
        )} ${startHour}:00:00`,
        end_time: `${dayjs(toDate, "YYYYMMDD").format("YYYY-MM-DD")} ${endHour}:59:59`,
        metrics_name: startHour == "00" && endHour == "23" ? dayMetrics : hourMetrics,
      });
    } else {
      if (startHour != "00") {
        timeRanges.push({
          start_time: `${dayjs(fromDate, "YYYYMMDD").format(
            "YYYY-MM-DD"
          )} ${startHour}:00:00`,
          end_time: `${dayjs(fromDate, "YYYYMMDD").format("YYYY-MM-DD")} 23:59:59`,
          metrics_name: hourMetrics,
        });
      }
      if (toDate !== currentUtcDate && toDate !== previousUtcDate) {
        if (endHour != "23") {
          timeRanges.push({
            start_time: `${dayjs(toDate, "YYYYMMDD").format("YYYY-MM-DD")} 00:00:00`,
            end_time: `${dayjs(toDate, "YYYYMMDD").format(
              "YYYY-MM-DD"
            )} ${endHour}:59:59`,
            metrics_name: hourMetrics,
          });
        }
        let val = {
          start_time: `${dayjs(fromDate, "YYYYMMDD")
            .add(startHour != "00" ? 1 : 0, "day")
            .format("YYYY-MM-DD")} 00:00:00`,
          end_time: `${dayjs(toDate, "YYYYMMDD")
            .subtract(endHour != "23" ? 1 : 0, "day")
            .format("YYYY-MM-DD")} 23:59:59`,
          metrics_name: dayMetrics,
        };

        if (dayjs(val.start_time).isBefore(dayjs(val.end_time))) {
          timeRanges.push(val);
        }
      } else {
        let val = {
          start_time: `${dayjs(fromDate, "YYYYMMDD")
            .add(startHour != "00" ? 1 : 0, "day")
            .format("YYYY-MM-DD")} 00:00:00`,
          end_time: `${dayjs().utc().subtract(2, "day").format("YYYY-MM-DD")} 23:59:59`,
          metrics_name: dayMetrics,
        };

        if (dayjs(val.start_time).isBefore(dayjs(val.end_time))) {
          timeRanges.push(val);
        }
        targetDates =
          toDate === currentUtcDate
            ? [previousUtcDate, currentUtcDate]
            : [previousUtcDate];
      }
    }
  } else {
    targetDates =
      toDate === currentUtcDate
        ? fromDate === currentUtcDate
          ? [currentUtcDate]
          : [previousUtcDate, currentUtcDate]
        : [previousUtcDate];
  }

  let metricsNames = [hourMetrics, rawMetrics];
  targetDates.forEach((targetDate) => {
    const targetStartHour = targetDate === fromDate ? parseInt(startHour, 10) : 0;
    const targetEndHour = targetDate === toDate ? parseInt(endHour, 10) : 23;
    generateCountsTimeRangeByDate(targetDate, targetStartHour, targetEndHour).forEach(
      (range, index) => {
        if (Object.keys(range).length > 0) {
          timeRanges.push({ ...range, metrics_name: metricsNames[index] });
        }
      }
    );
  });
  return timeRanges.filter(
    (item) => item.start_time && item.end_time && item.metrics_name
  );
}

export {
  sortKeysRecursively,
  getCountsData,
  generateHourAndRawMetricsTimeRanges,
  generateDayAndHourAndRawMetricsTimeRanges,
};
